import './styles.scss'
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Skeleton } from 'primereact/skeleton'
import { getLocalLoggedInStatus, getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import { supabase } from 'Services/supabaseClient'
import { MessageMenu } from 'Components/UIComponent/FloatingMenu'
import AttachmentMenu from 'Components/UIComponent/FloatingMenu/Attachment'
import AttachmentPreview from 'Components/UIComponent/AttachmentPreview'
import { useResizeHandler, urlMethods } from 'Utils/commonMethods'
import URLPreview from 'Components/UIComponent/URLPreview'
import { useUrlMetadata } from 'Utils/useUrlMetadata'
import { getURLMetadata } from 'Services/apiAdapter_CoffeeWeb'
import { ROUTE_STRINGS } from 'Utils/Constants'
import DialogUIModal from 'Components/UIComponent/DialogUIModal'
import PersonalChatMessage from './PersonalChatMessage'
import ForwardMessageModal from './ForwardMessageModal'
import EditMessageModal from './EditMessageModal'
import { COFFEE_COMMUNITIES_METHODS, COFFEE_COMMUNITIES_CONSTANTS, useImageZoom, AttachmentViewerControls } from '../CoffeeCommunities/Utils'
import ChatControls from './ChatControls'

const PersonalChat = (props) => {
  const { isCoffeePost, selectedUserId, onUserSelect, onBackToPersonalChats, hasHeader, hideSidebar, renderSidebar, onSidebarReady, onUnreadCountRefresh } = props
  const { timeMethods, attachmentMethods } = COFFEE_COMMUNITIES_METHODS
  const { MAX_FILE_SIZE, MAX_FILES, RETRY_DELAY, MAX_RETRIES, BUCKET_NAME } = COFFEE_COMMUNITIES_CONSTANTS
  const { t } = useTranslation()
  const history = useHistory()
  const { isWebView } = useResizeHandler()
  const userLoginStatus = getLocalLoggedInStatus()

  const [searchTerm, setSearchTerm] = useState('')
  const [value, setValue] = useState('')
  const [chats, setChats] = useState([])
  const [personalChats, setPersonalChats] = useState([])
  const [filteredPersonalChats, setFilteredPersonalChats] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedChatUser, setSelectedChatUser] = useState(null)
  const [currentChatId, setCurrentChatId] = useState(null)
  const [isFetchingOlder, setIsFetchingOlder] = useState(false)
  const [repliedMessage, setRepliedMessage] = useState(null)
  const [internalSelectedUserId, setInternalSelectedUserId] = useState(null)
  const [isUserBlocked, setIsUserBlocked] = useState(false)

  const userDetails = getLocalUserDetails()
  const inputRef = useRef(null)
  const containerRef = useRef(null)
  const repliedMsgRef = useRef(null)
  const uploaderRef = useRef(null)
  const PAGE_SIZE = 20
  const existingIdsRef = useRef(new Set())
  const isInitialLoadRef = useRef(true)
  const isFetchingRef = useRef(false)
  const hasMoreRef = useRef(true)
  const atBottomRef = useRef(true)
  const BOTTOM_EPS = 24
  const processingUserIdRef = useRef(null) // Track which userId is currently being processed
  const fetchingChatIdRef = useRef(null) // Track which chatId is currently being fetched
  const isFetchingChatListRef = useRef(false) // Track if chat list is currently being fetched
  const chatListDebounceTimerRef = useRef(null) // Debounce timer for chat list updates
  const prevChatsLengthRef = useRef(0) // Track previous chat length to detect if messages were added at bottom
  const prevLastMessageIdRef = useRef(null) // Track last message ID to detect if messages were added at bottom
  const isFetchingOlderRef = useRef(false) // Track if we're currently fetching older messages
  const isUpdatingUnreadCountRef = useRef(false) // Track if we're updating unread count ourselves (to skip subscription refresh)
  const isSendingMessageRef = useRef(false) // Track if we're sending a message (to skip chat list refresh)

  // Viewer state for attachments
  const [viewer, setViewer] = useState({
    open: false,
    type: null,
    url: '',
    name: '',
    attachments: [], // All attachments in the message
    currentIndex: 0 // Current attachment being viewed
  })

  const [isFullscreen, setIsFullscreen] = useState(false)

  // Use reusable zoom hook
  const { zoomContainerRef, imgRef, zoomRefs, handleWheelZoom, handlePointerDown, handlePointerMove, handlePointerUp, handleZoomIn, handleZoomOut, getTransformStyle, zoomLevel } = useImageZoom(viewer.url)

  const [attachments, setAttachments] = useState([]) // array of { file, previewUrl, type: 'media'|'doc', name, size }
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [showRedirectText, setShowRedirectText] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState(false)
  const [modalAction, setModalAction] = useState(null) // 'deleteForMe' | 'deleteForEveryone'
  const [modalMessage, setModalMessage] = useState(null)

  // URL preview state for input area
  const [inputPreviewUrl, setInputPreviewUrl] = useState(null)
  const [inputPreviewMetadata, setInputPreviewMetadata] = useState(null)

  // Edit message state
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [messageToEdit, setMessageToEdit] = useState(null)

  // Forward message state
  const [forwardModalVisible, setForwardModalVisible] = useState(false)
  const [messageToForward, setMessageToForward] = useState(null)
  const [isForwarding, setIsForwarding] = useState(false)

  // Communities state for forwarding
  const [communities, setCommunities] = useState([])
  const [isAdminUser, setIsAdminUser] = useState(false)

  // Helper function to determine attachment type from multiple sources
  const getAttachmentType = useCallback((attachment) => {
    if (!attachment) return 'document'

    // First, check if attachment_type is already set and valid
    if (attachment.attachment_type) {
      const attType = attachment.attachment_type.toLowerCase()

      if (attType === 'image' || attType.startsWith('image')) return 'image'
      if (attType === 'video' || attType.startsWith('video')) return 'video'
      if (attType === 'document' || attType.startsWith('document')) return 'document'
    }

    // Second, check mime_type
    const mimeType = (attachment.mime_type || attachment.mime || '').toLowerCase()

    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('video/')) return 'video'

    // Third, check file extension from file_name
    const fileName = (attachment.file_name || attachment.name || '').toLowerCase()
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v']

    if (imageExtensions.some((ext) => fileName.endsWith(ext))) return 'image'
    if (videoExtensions.some((ext) => fileName.endsWith(ext))) return 'video'

    // Default to document
    return 'document'
  }, [])

  // Helpers to enrich messages with their replied-to message content/user info
  const getAttachmentPreviewMeta = useCallback(
    (msg) => {
      const attachments = Array.isArray(msg?.attachments) ? msg.attachments : []

      let attachmentsType = null

      if (attachments.length) {
        // Use the helper function to check attachment types
        const hasVideo = attachments.some((att) => getAttachmentType(att) === 'video')
        const hasImage = attachments.some((att) => getAttachmentType(att) === 'image')

        if (hasVideo) attachmentsType = 'video'
        else if (hasImage) attachmentsType = 'image'
        else attachmentsType = 'document'
      }

      return { attachmentsType, attachmentsCount: attachments }
    },
    [getAttachmentType]
  )

  const enrichMessagesWithReplies = useCallback(
    (messages) => {
      const lookup = new Map()

      messages.forEach((m) => {
        if (m?.id) lookup.set(String(m.id), m)
      })

      return messages.map((m) => {
        if (!m?.reply_to_id) return m

        const replied = lookup.get(String(m.reply_to_id))

        if (!replied) return m

        const { attachmentsType, attachmentsCount } = getAttachmentPreviewMeta(replied)

        return {
          ...m,
          repliedMessage: {
            id: replied.id,
            content: replied.content,
            user_info: replied.user_info || replied.user || null,
            attachmentsType,
            attachmentsCount,
            attachments: replied.attachments || [],
            is_reply_deleted: replied.deleted_for_sender || replied.deleted_for_receiver || false
          }
        }
      })
    },
    [getAttachmentPreviewMeta]
  )

  // Determine if we should show sidebar or chat in CoffeePostScreen mode
  const activeSelectedUserId = selectedUserId || internalSelectedUserId
  const showChatInCoffeePost = isCoffeePost && activeSelectedUserId

  // Format time utility
  const formatTime = useCallback(
    (ts) => {
      const use12h = userDetails?.id ? userDetails.userTimeFormat?.startsWith('12') : false

      return timeMethods.UtcToLocalTimeFn(ts, use12h)
    },
    [userDetails?.id, userDetails?.userTimeFormat]
  )

  // Format date utility (for date labels)
  const formatDate = useCallback((timestamp) => {
    const date = new Date(timestamp)
    const today = new Date()
    const yesterday = new Date(today)

    yesterday.setDate(today.getDate() - 1)

    const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()
    const isYesterday = date.getDate() === yesterday.getDate() && date.getMonth() === yesterday.getMonth() && date.getFullYear() === yesterday.getFullYear()

    if (isToday) {
      return 'Today'
    }

    if (isYesterday) {
      return 'Yesterday'
    }

    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }, [])

  // Group messages by date
  const groupedMessages = useMemo(() => {
    const sections = []
    const indexByLabel = Object.create(null)

    return chats.reduce((acc, m) => {
      const label = formatDate(m.timestamp)

      let idx = indexByLabel[label]

      if (idx === undefined) {
        idx = acc.length
        indexByLabel[label] = idx
        acc.push([label, []])
      }

      acc[idx][1].push(m)

      return acc
    }, sections)
  }, [chats, formatDate])

  // --- URL metadata for input ---

  const firstUrlInInput = urlMethods.getFirstUrl(value)
  const { metadata: inputMetadata, isLoading: isInputMetadataLoading } = useUrlMetadata(firstUrlInInput, Boolean(firstUrlInInput))

  useEffect(() => {
    if (firstUrlInInput) {
      setInputPreviewUrl(firstUrlInInput)

      if (inputMetadata) {
        setInputPreviewMetadata(inputMetadata)
      } else if (!isInputMetadataLoading) {
        // Clear metadata if loading is done but no metadata (error or invalid response)
        setInputPreviewMetadata(null)
      }
    } else {
      setInputPreviewUrl(null)
      setInputPreviewMetadata(null)
    }
  }, [firstUrlInInput, inputMetadata, isInputMetadataLoading])

  // Global paste listener - works even when AttachmentPreview is showing
  useEffect(() => {
    const handleWindowPaste = (e) => {
      // Only handle if user is in the chat area (not typing elsewhere)
      // Check if active element is not a text input outside our component
      const isInOurComponent = e.target.closest('.chat-input') || e.target.closest('.input-area') || e.target.closest('.input-area-container')

      // Call our paste handler if we're in the right area or if it's a global paste
      if (!e.target.matches('input[type="text"], textarea, [contenteditable="true"]') || isInOurComponent) {
        handlePaste(e)
      }
    }

    // Listen for paste events on window
    document.addEventListener('paste', handleWindowPaste, true)

    return () => {
      document.removeEventListener('paste', handleWindowPaste, true)
    }
  }, [attachments]) // Re-attach when attachments change

  // File handling functions
  const addFiles = (fileList, kind) => {
    if (!fileList || !fileList.length) return

    const incoming = fileList.map((f, i) => ({
      file: f,
      name: f.name,
      size: f.size,
      mime: f.type,
      kind, // 'media' or 'doc'
      previewUrl: kind === 'media' ? URL.createObjectURL(f) : null,
      id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      caption: i === 0 ? value?.trim() || '' : '',
      sourceMessage: i === 0 ? Boolean(value?.trim()) : false
    }))

    const errors = []
    const allowedNow = MAX_FILES - attachments.length

    if (incoming.length > allowedNow) {
      errors.push(`You can attach up to ${MAX_FILES} files. You can add ${allowedNow} more.`)
    }

    const validated = []

    for (let i = 0; i < Math.min(incoming.length, allowedNow); i++) {
      const it = incoming[i]

      let valid = true

      if (it.size > MAX_FILE_SIZE) {
        errors.push(`${it.name} exceeds maximum size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`)
        valid = false
      }

      if (valid && kind === 'media') {
        const ok = /^(image|video)\//.test(it.mime)

        if (!ok) {
          errors.push(`${it.name} is not a supported media file`)
          valid = false
        }
      } else if (valid) {
        const ok = /\.(pdf|docx|doc|xls|xlsx|csv|txt)$/i.test(it.name)

        if (!ok) {
          errors.push(`${it.name} is not a supported document type`)
          valid = false
        }
      }

      if (valid) validated.push(it)
    }

    if (errors.length) {
      alert(errors.join('\n'))
    }

    return validated
  }

  const handleMediaSelected = (files) => {
    const newAttachments = addFiles(files, 'media')

    if (newAttachments?.length) {
      setAttachments((prev) => [...prev, ...newAttachments])
      setValue('')
    }
  }

  const handleDocumentSelected = (files) => {
    const newAttachments = addFiles(files, 'doc')

    if (newAttachments?.length) {
      setAttachments((prev) => [...prev, ...newAttachments])
      setValue('')
    }
  }

  const handlePaste = (e) => {
    const clipboard = e.clipboardData

    if (!clipboard) return

    const imageFiles = Array.from(clipboard.files).filter((file) => file.type.startsWith('image/'))

    if (imageFiles.length === 0) return

    e.preventDefault()

    // Important: clone file into new File object with unique naming
    const timestamp = Date.now()
    const randomId = Math.random().toString(36).slice(2, 8)

    const clonedFiles = imageFiles.map(
      (file, index) =>
        new File([file], `${timestamp}-${index}-${randomId}-${file.name}`, {
          type: file.type
        })
    )

    const newAttachments = addFiles(clonedFiles, 'media')

    if (newAttachments?.length) {
      setAttachments((prev) => [...prev, ...newAttachments])
    }

    // Force focus back immediately after paste - multiple times to ensure it sticks
    if (inputRef.current) {
      inputRef.current.focus()
    }

    // Double-check focus after state updates complete
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 0)
  }

  const removeAttachment = (attId) => {
    setAttachments((prev) => {
      const found = prev.find((p) => p.id === attId)

      if (found && found.previewUrl) URL.revokeObjectURL(found.previewUrl)

      return prev.filter((p) => p.id !== attId)
    })
  }

  const onUpdateCaption = (id, { caption }) => {
    setAttachments((prev) => prev.map((att) => (att.id === id ? { ...att, caption } : att)))
  }

  // Upload to Supabase Storage
  const uploadToSupabaseStorage = async (file, chatId) => {
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File exceeds maximum size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`)
    }

    if (file.size === 0) {
      throw new Error('File is empty')
    }

    // Generate file path with folder structure
    const safeChatId = attachmentMethods.sanitizeFileName((chatId || 'personal_chat').toString())
    const timestamp = Date.now()
    const sanitizedFileName = attachmentMethods.sanitizeFileName(file.name)
    const uniqueFileName = `${timestamp}_${sanitizedFileName}`
    const filePath = safeChatId ? `personal_chat/${safeChatId}/${uniqueFileName}` : `personal_chat/${uniqueFileName}`

    // Retry logic with Promise.all to handle parallel uploads
    const uploadAttempt = async () => {
      // Use .upload() for all files (Supabase JS SDK handles buffering efficiently)
      const { data, error } = await supabase.storage.from(BUCKET_NAME).upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || 'application/octet-stream'
      })

      if (error) {
        if (error.message.includes('duplicate') || error.message.includes('already exists')) {
          const { data: retryData, error: retryError } = await supabase.storage.from(BUCKET_NAME).upload(filePath, file, {
            cacheControl: '3600',
            upsert: true,
            contentType: file.type || 'application/octet-stream'
          })

          if (retryError) throw retryError
        } else {
          throw error
        }
      }

      // Get public URL
      const { data: urlData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(filePath)
      const publicUrl = urlData.publicUrl

      return {
        url: publicUrl,
        media_url: publicUrl,
        s3_key: filePath,
        s3_bucket: BUCKET_NAME,
        name: file.name,
        mime: file.type,
        size: file.size
      }
    }

    // Implement retry logic using Promise and recursion instead of loop
    const attemptUpload = async (attemptsLeft) => {
      try {
        return await uploadAttempt()
      } catch (error) {
        if (attemptsLeft <= 1) {
          throw error
        }

        // Create delay promise
        const delay = () =>
          new Promise((resolve) => {
            setTimeout(resolve, RETRY_DELAY)
          })

        // Wait for delay then recursively try again
        await delay()

        return attemptUpload(attemptsLeft - 1)
      }
    }

    // Start recursive retry chain
    try {
      return await attemptUpload(MAX_RETRIES)
    } catch (error) {
      throw error || new Error('Upload failed after maximum retry attempts')
    }
  }

  // Fetch personal chat list with debouncing and duplicate prevention
  const fetchPersonalChatList = useCallback(
    async (skipDebounce = false) => {
      if (!userDetails?.id) {
        setIsLoading(false)

        return
      }

      // Skip when current user is sending a message - avoid unnecessary refresh
      if (isSendingMessageRef.current) {
        return
      }

      // Prevent duplicate concurrent calls
      if (isFetchingChatListRef.current) {
        return
      }

      // Debounce function to batch rapid calls
      const executeFetch = async () => {
        if (isFetchingChatListRef.current) {
          return
        }

        isFetchingChatListRef.current = true
        setIsLoading(true)

        try {
          const { data, error } = await supabase.rpc('get_personal_chat_list', {
            p_user_id: userDetails.id
          })

          if (error) {
            setPersonalChats([])
            setFilteredPersonalChats([])

            return
          }

          const chatList = Array.isArray(data) ? data : []

          setPersonalChats(chatList)
          setFilteredPersonalChats(chatList)
        } catch (err) {
          setPersonalChats([])
          setFilteredPersonalChats([])
        } finally {
          setIsLoading(false)
          isFetchingChatListRef.current = false
        }
      }

      // Clear existing debounce timer
      if (chatListDebounceTimerRef.current) {
        clearTimeout(chatListDebounceTimerRef.current)
        chatListDebounceTimerRef.current = null
      }

      // Execute immediately if skipDebounce is true, otherwise debounce
      if (skipDebounce) {
        await executeFetch()
      } else {
        chatListDebounceTimerRef.current = setTimeout(() => {
          executeFetch()
          chatListDebounceTimerRef.current = null
        }, 300) // 300ms debounce delay
      }
    },
    [userDetails?.id]
  )

  // Check permission and initiate chat
  const checkPermissionAndInitiateChat = useCallback(
    async (otherUserId) => {
      if (!userDetails?.id || !otherUserId) return null

      try {
        // Check permission
        const { data: permissionData, error: permissionError } = await supabase.rpc('check_personal_chat_permission', {
          p_user1_id: userDetails.id,
          p_user2_id: otherUserId
        })

        if (permissionError) {
          return null
        }

        if (!permissionData) {
          return null
        }

        // Initiate chat
        const { data: chatData, error: chatError } = await supabase.rpc('initiate_personal_chat', {
          p_user1_id: userDetails.id,
          p_user2_id: otherUserId
        })

        if (chatError) {
          return null
        }

        return chatData
      } catch (err) {
        return null
      }
    },
    [userDetails?.id]
  )

  // Check if user is blocked
  const checkBlockedStatus = useCallback(
    async (otherUserId) => {
      if (!userDetails?.id || !otherUserId) {
        setIsUserBlocked(false)

        return
      }

      try {
        const { data, error } = await supabase.rpc('check_personal_chat_blocked', {
          p_blocker_id: userDetails.id,
          p_blocked_user_id: otherUserId
        })

        if (error) {
          setIsUserBlocked(false)

          return
        }

        setIsUserBlocked(Boolean(data?.is_blocked))
      } catch (err) {
        setIsUserBlocked(false)
      }
    },
    [userDetails?.id]
  )

  // Update personal chat unread count and last read timestamp
  const updatePersonalChatUnreadCount = useCallback(
    async (chatId, skipRefresh = false) => {
      if (!chatId || !userDetails?.id) return

      try {
        // Fetch the chat record to determine user1 and user2
        const { data: chatRecord, error: chatError } = await supabase.from('personal_chats').select('user1_id, user2_id, user1_unread_count, user2_unread_count').eq('id', chatId).single()

        if (chatError || !chatRecord) {
          return
        }

        // Determine if current user is user1 or user2
        const isUser1 = String(chatRecord.user1_id) === String(userDetails.id)
        const isUser2 = String(chatRecord.user2_id) === String(userDetails.id)

        if (!isUser1 && !isUser2) {
          return
        }

        // Prepare update object based on which user is opening the chat
        const updateData = {}
        const currentTime = new Date().toISOString()

        if (isUser1) {
          // Update user1 fields if unread count is greater than 0 or if last_read needs updating
          if (chatRecord.user1_unread_count > 0 || !chatRecord.user1_last_read) {
            updateData.user1_unread_count = 0
            updateData.user1_last_read = currentTime
          }
        } else if (isUser2) {
          // Update user2 fields if unread count is greater than 0 or if last_read needs updating
          if (chatRecord.user2_unread_count > 0 || !chatRecord.user2_last_read) {
            updateData.user2_unread_count = 0
            updateData.user2_last_read = currentTime
          }
        }

        // Only update if there are changes to make
        if (Object.keys(updateData).length > 0) {
          // Set flag to prevent subscription from triggering refresh
          isUpdatingUnreadCountRef.current = true

          const { error: updateError } = await supabase.from('personal_chats').update(updateData).eq('id', chatId)

          if (updateError) {
            // console.error('Error updating personal chat unread count:', updateError)
            isUpdatingUnreadCountRef.current = false
          } else {
            // Update local state immediately so unread badge clears without needing fetch (works even when isSendingMessageRef blocks fetch)
            const matchesChat = (chat) => String(chat.chat_id || chat.id) === String(chatId)

            setPersonalChats((prev) => prev.map((chat) => (matchesChat(chat) ? { ...chat, unread_count: 0 } : chat)))
            setFilteredPersonalChats((prev) => prev.map((chat) => (matchesChat(chat) ? { ...chat, unread_count: 0 } : chat)))
            onUnreadCountRefresh?.()

            // Refresh chat list to update unread count in UI (skip debounce for immediate update)
            // Skip refresh if called during initial load to prevent duplicate calls and skeleton showing
            if (!skipRefresh) {
              fetchPersonalChatList(true)
            }
            // Clear flag after a short delay to allow subscription to work normally for other updates
            setTimeout(() => {
              isUpdatingUnreadCountRef.current = false
            }, 500)
          }
        }
      } catch (err) {
        // console.error('Error in updatePersonalChatUnreadCount:', err)
      }
    },
    [userDetails?.id, fetchPersonalChatList, onUnreadCountRefresh]
  )

  // Fetch personal messages with pagination
  const fetchPersonalMessages = useCallback(
    async (chatId, isInitialLoad = false) => {
      if (!chatId || !userDetails?.id) return

      // Prevent duplicate fetches
      if (isFetchingRef.current) return

      // Prevent duplicate initial loads for the same chatId
      if (isInitialLoad && fetchingChatIdRef.current === chatId) {
        return
      }

      if (isInitialLoad) {
        fetchingChatIdRef.current = chatId // Mark this chatId as being fetched
        isInitialLoadRef.current = true
        hasMoreRef.current = true
        existingIdsRef.current = new Set()
        setChats([])
      }

      isFetchingRef.current = true
      setIsFetchingOlder(true)
      isFetchingOlderRef.current = !isInitialLoad // Track if we're fetching older messages (not initial load)

      const container = containerRef.current
      const prevScrollHeight = container ? container.scrollHeight : 0
      const prevScrollTop = container ? container.scrollTop : 0

      try {
        const offset = existingIdsRef.current.size

        const { data, error } = await supabase.rpc('get_personal_messages_with_attachments', {
          p_chat_id: chatId,
          p_user_id: userDetails.id,
          p_limit: PAGE_SIZE,
          p_offset: offset
        })

        if (error) {
          hasMoreRef.current = false

          return
        }

        const messages = Array.isArray(data?.messages) ? data.messages : []

        // Transform and filter messages
        const transformedMessages = messages
          .filter((msg) => {
            // Filter out messages already in chat
            if (existingIdsRef.current.has(String(msg.id))) return false

            // Filter deleted messages based on sender/receiver flags
            const isSender = msg.sender_id === userDetails.id

            if (isSender && msg.deleted_for_sender) return false
            if (!isSender && msg.deleted_for_receiver) return false

            return true
          })
          .map((msg) => ({
            // Transform message structure to match ChatMessage expectations
            ...msg,
            userid: msg.sender_id, // ChatMessage uses userid for isOutgoing check
            user_info: msg.sender_info || {
              userid: msg.sender_id,
              first_name: '',
              last_name: '',
              users_flag_url: ''
            }
          }))

        hasMoreRef.current = messages.length === PAGE_SIZE

        if (!transformedMessages.length) {
          // Still update unread count even if no messages
          if (isInitialLoad) {
            await updatePersonalChatUnreadCount(chatId, true) // Skip refresh during initial load
          }

          return
        }

        // Update refs
        transformedMessages.forEach((msg) => {
          existingIdsRef.current.add(String(msg.id))
        })

        // Sort chronologically
        const sortedMessages = transformedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))

        // ========== FETCH URL PREVIEWS FOR MESSAGES WITH URLs ==========
        // Helper function to fetch URL metadata
        const fetchUrlPreview = async (url) => {
          try {
            const response = await getURLMetadata(url, null)
            const urlMetaData = response?.data || response

            if (urlMetaData && typeof urlMetaData === 'object' && !Array.isArray(urlMetaData) && (urlMetaData.title || urlMetaData.description || urlMetaData.image)) {
              const domain = urlMethods.extractDomainFromUrl(url)
              const title = urlMetaData.title && urlMetaData.title !== url ? urlMetaData.title : domain

              return {
                title: title || domain,
                description: urlMetaData.description || '',
                image: urlMetaData.image || '',
                url,
                siteName: urlMetaData.siteName || domain
              }
            }
          } catch (err) {
            // console.error('Error fetching URL preview:', err)
          }

          return null
        }

        // Identify messages that need URL previews (have URLs but no preview metadata)
        const messagesNeedingPreview = sortedMessages.filter((msg) => {
          const hasUrl = urlMethods.hasUrlInMessage(msg.content || '', msg.attachments || [])

          return hasUrl && !msg.url_preview
        })

        // Fetch previews in parallel
        const previewPromises = messagesNeedingPreview.map(async (msg) => {
          const firstUrl = urlMethods.getFirstUrl(msg.content || '')

          if (firstUrl) {
            const preview = await fetchUrlPreview(firstUrl)

            return { messageId: msg.id, preview }
          }

          return { messageId: msg.id, preview: null }
        })

        const previewResults = await Promise.all(previewPromises)
        const previewMap = {}

        previewResults.forEach(({ messageId, preview }) => {
          if (preview) {
            previewMap[messageId] = preview
          }
        })

        // Attach URL previews to messages
        const messagesWithPreviews = sortedMessages.map((msg) => ({
          ...msg,
          url_preview: msg.url_preview || previewMap[msg.id] || null
        }))

        if (isInitialLoad) {
          setChats((prevOptimistic) => {
            // Filter out optimistic messages and ensure no duplicates
            const optimistic = prevOptimistic.filter((p) => !p.id)
            const existingIds = new Set(optimistic.map((m) => String(m.id)))
            const newMessages = messagesWithPreviews.filter((m) => !existingIds.has(String(m.id)))

            return enrichMessagesWithReplies([...newMessages, ...optimistic])
          })
          isInitialLoadRef.current = false

          // Reset unread count when opening personal chat
          await updatePersonalChatUnreadCount(chatId, true) // Skip refresh during initial load to prevent duplicate calls

          requestAnimationFrame(() => {
            if (containerRef.current) {
              containerRef.current.scrollTop = containerRef.current.scrollHeight
            }
          })
        } else {
          setChats((prev) => {
            // Prevent duplicates when loading older messages
            const existingIds = new Set(prev.map((m) => String(m.id)))
            const newMessages = messagesWithPreviews.filter((m) => !existingIds.has(String(m.id)))

            return enrichMessagesWithReplies([...newMessages, ...prev])
          })

          requestAnimationFrame(() => {
            if (!containerRef.current) return
            const newScrollHeight = containerRef.current.scrollHeight
            const delta = newScrollHeight - prevScrollHeight

            containerRef.current.scrollTop = prevScrollTop + delta
          })
        }
      } catch (err) {
        hasMoreRef.current = false
      } finally {
        isFetchingRef.current = false
        setIsFetchingOlder(false)
        isFetchingOlderRef.current = false // Reset after fetch completes
        if (isInitialLoad) {
          fetchingChatIdRef.current = null // Clear after fetch completes
        }
      }
    },
    [userDetails?.id, PAGE_SIZE, updatePersonalChatUnreadCount, enrichMessagesWithReplies]
  )

  // Handle user selection
  const handleUserSelect = useCallback(
    async (userId) => {
      if (!userId || !userDetails?.id) return

      // Prevent duplicate processing for the same userId
      if (processingUserIdRef.current === userId) {
        return
      }

      // Prevent if already loaded and matches current chat
      if (internalSelectedUserId === userId && currentChatId && !isFetchingRef.current && chats.length > 0) {
        return
      }

      processingUserIdRef.current = userId // Mark as processing

      // Find user in chat list - use other_user_id from API response
      let chatUser = personalChats.find((chat) => String(chat.other_user_id || chat.user_id || chat.id) === String(userId))

      // If user not found in chat list, fetch user info and create a temporary chat user object
      if (!chatUser) {
        try {
          // Fetch user info from database
          const { data: userInfo, error: userError } = await supabase.from('users').select('userid, first_name, last_name, users_flag_url, allow_personal_chat').eq('userid', userId).single()

          if (userError || !userInfo) {
            processingUserIdRef.current = null // Clear on error

            return
          }

          // Create a temporary chat user object
          chatUser = {
            other_user_id: userId,
            other_user_name: `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim() || 'User',
            other_user_avatar_url: userInfo.users_flag_url || '',
            chat_id: null, // Will be set after initiating chat
            last_message: null,
            unread_count: 0
          }
        } catch (err) {
          processingUserIdRef.current = null // Clear on error

          return
        }
      }

      setSelectedChatUser(chatUser)

      // If chat_id already exists in the response, use it directly
      let chatId = chatUser.chat_id

      // If no chat_id, check permission and initiate chat
      if (!chatId) {
        const chatData = await checkPermissionAndInitiateChat(userId)

        if (!chatData) {
          processingUserIdRef.current = null // Clear on error

          return
        }
        chatId = chatData.chat_id || chatData.id

        // Update chatUser with the new chat_id
        chatUser.chat_id = chatId
        setSelectedChatUser(chatUser)

        // Refresh chat list to include the new chat (skip debounce for immediate update)
        fetchPersonalChatList(true)
      }

      if (!chatId) {
        processingUserIdRef.current = null // Clear on error

        return
      }

      // Only reset and fetch if chatId changed or no messages loaded
      if (currentChatId !== chatId || chats.length === 0) {
        setCurrentChatId(chatId)
        setInternalSelectedUserId(userId) // Set internal state to show chat

        // Reset chat state
        isInitialLoadRef.current = true
        hasMoreRef.current = true
        existingIdsRef.current = new Set()
        setChats([])
        setValue('')
        setRepliedMessage(null)
        setAttachments([])

        // Check blocked status
        await checkBlockedStatus(userId)

        // Fetch messages
        await fetchPersonalMessages(chatId, true)
      } else {
        // Check blocked status even if chat is already loaded
        await checkBlockedStatus(userId)
      }

      // Clear processing flag before notifying parent
      processingUserIdRef.current = null

      // Notify parent if callback exists (only if userId is different from selectedUserId to prevent loops)
      // This prevents the parent from updating selectedUserId which would trigger useEffect again
      if (onUserSelect && String(selectedUserId) !== String(userId)) {
        onUserSelect(userId)
      }
    },
    [userDetails?.id, personalChats, checkPermissionAndInitiateChat, fetchPersonalMessages, onUserSelect, selectedUserId, fetchPersonalChatList, currentChatId, internalSelectedUserId, chats.length, checkBlockedStatus]
  )

  // Send message
  const sendMessage = useCallback(async () => {
    if (!currentChatId || !userDetails?.id) return

    const trimmedValue = value.trim()
    const hasAttachments = attachments.length > 0

    if (!trimmedValue && !hasAttachments) return

    isSendingMessageRef.current = true
    const tempId = `temp-${Date.now()}`
    let uploadedAttachments = []

    // Capture current URL preview state before clearing input
    const previewMetadataToKeep = inputPreviewMetadata
    const previewUrlToKeep = inputPreviewUrl

    // Prepare attachments for optimistic message (using preview URLs)
    const optimisticAttachments = attachments.map((att) => {
      // Determine attachment type for optimistic message
      let initialAttachmentType = 'document'

      if (att.kind === 'media') {
        const mimeType = att.mime || att.file?.type || ''

        if (mimeType.startsWith('image/')) {
          initialAttachmentType = 'image'
        } else if (mimeType.startsWith('video/')) {
          initialAttachmentType = 'video'
        }
      }

      const attachmentType = getAttachmentType({
        mime_type: att.mime || att.file?.type || '',
        file_name: att.name || att.file?.name || '',
        attachment_type: initialAttachmentType
      })

      return {
        media_url: att.previewUrl || '',
        attachment_url: att.previewUrl || '',
        mime_type: att.mime || att.file?.type || '',
        attachment_type: attachmentType,
        file_name: att.name || att.file?.name || '',
        caption: att.caption || '',
        pending: true // Mark as pending for spinner
      }
    })

    // Detect first URL in message content
    const firstUrl = urlMethods.getFirstUrl(trimmedValue)

    // Create optimistic message with attachments and URL preview (if available)
    const newMessage = {
      id: tempId,
      chat_id: currentChatId,
      sender_id: userDetails.id,
      userid: userDetails.id, // Add userid for ChatMessage compatibility
      content: trimmedValue,
      type: attachments.length ? 'file' : 'text',
      reply_to_id: repliedMessage?.id || null,
      repliedMessage: repliedMessage || null,
      timestamp: new Date().toISOString(),
      pending: true,
      attachments: optimisticAttachments,
      url_preview: previewMetadataToKeep || null,
      url_preview_url: firstUrl || null,
      user_info: {
        userid: userDetails.id,
        first_name: userDetails.first_name || '',
        last_name: userDetails.last_name || '',
        users_flag_url: userDetails.users_flag_url || ''
      }
    }

    // Immediately clear attachments and show uploading state
    const attachmentsToUpload = [...attachments]
    const inputValueRef = trimmedValue

    // Clear state immediately
    setAttachments([])
    setValue('')
    setRepliedMessage(null)
    setIsUploadingImage(true)

    // Add optimistic message to chat immediately
    setChats((prev) => enrichMessagesWithReplies([...prev, newMessage]))

    // Scroll to bottom instantly
    requestAnimationFrame(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight
      }
    })

    try {
      // If has attachments, upload them first
      if (attachmentsToUpload.length) {
        uploadedAttachments = await Promise.all(attachmentsToUpload.map((att) => uploadToSupabaseStorage(att.file, currentChatId)))
      }

      const attachmentsPayload = uploadedAttachments.map((att, index) => {
        const originalAttachment = attachmentsToUpload[index]

        // Determine initial attachment type based on kind
        let initialAttachmentType = 'document'

        if (originalAttachment.kind === 'media') {
          const mimeType = originalAttachment.file.type || ''

          if (mimeType.startsWith('image/')) {
            initialAttachmentType = 'image'
          } else if (mimeType.startsWith('video/')) {
            initialAttachmentType = 'video'
          }
        }

        // Use helper function to determine attachment type
        const attachmentType = getAttachmentType({
          mime_type: originalAttachment.file.type || '',
          file_name: originalAttachment.file.name || '',
          attachment_type: initialAttachmentType
        })

        return {
          attachment_type: attachmentType,
          attachment_url: att.media_url || att.url, // Use media_url if available, fallback to url
          thumbnail_url: '', // Personal chat doesn't use thumbnails yet
          file_name: originalAttachment.file.name,
          file_size: originalAttachment.file.size,
          caption: originalAttachment.caption || ''
        }
      })

      // Send message via RPC
      const { data, error } = await supabase.rpc('create_personal_message_with_attachments', {
        p_chat_id: currentChatId,
        p_sender_id: userDetails.id,
        p_content: inputValueRef,
        p_message_type: attachmentsPayload.length ? 'file' : 'text',
        p_reply_to_id: repliedMessage?.id || null,
        p_attachments: attachmentsPayload
      })

      if (error) throw error

      // Fetch the newly created message
      const { data: newMessageData } = await supabase.rpc('get_personal_messages_with_attachments', {
        p_chat_id: currentChatId,
        p_user_id: userDetails.id,
        p_limit: 1,
        p_offset: 0
      })

      if (newMessageData?.messages?.[0]) {
        const latestMessage = newMessageData.messages[0]

        // If message has a URL, ensure we have final preview metadata
        let finalPreviewMetadata = previewMetadataToKeep

        if (!finalPreviewMetadata && previewUrlToKeep) {
          try {
            const response = await getURLMetadata(previewUrlToKeep, null)
            const data = response?.data || response

            if (data && typeof data === 'object' && !Array.isArray(data) && (Object.hasOwn(data, 'title') || Object.hasOwn(data, 'description') || Object.hasOwn(data, 'image'))) {
              const domain = urlMethods.extractDomainFromUrl(previewUrlToKeep)
              const title = data.title && data.title !== previewUrlToKeep ? data.title : domain

              finalPreviewMetadata = {
                title: title || domain,
                description: data.description || '',
                image: data.image || '',
                url: previewUrlToKeep,
                siteName: data.siteName || domain
              }
            }
          } catch {
            // Silent fail – URL preview is optional
          }
        }

        // Transform message to match expected structure
        const transformedMessage = {
          ...latestMessage,
          userid: latestMessage.sender_id, // Add userid for ChatMessage compatibility
          user_info: latestMessage.sender_info || {
            userid: latestMessage.sender_id,
            first_name: '',
            last_name: '',
            users_flag_url: ''
          },
          url_preview: finalPreviewMetadata || latestMessage.url_preview || null,
          pending: false
        }

        // Replace optimistic message with real one
        setChats((prev) => {
          // Check if message was already added by real-time subscription
          const alreadyExists = prev.some((m) => String(m.id) === String(transformedMessage.id))

          if (alreadyExists) {
            // Message already exists (added by real-time), just remove optimistic message if it exists
            const filtered = prev.filter((msg) => msg.id !== tempId)

            return enrichMessagesWithReplies(filtered)
          }

          // Replace optimistic message with real one
          const updated = prev.map((msg) => (msg.id === tempId ? transformedMessage : msg))

          // Add real message ID to existingIdsRef to prevent duplicates from real-time subscription
          if (transformedMessage.id) {
            existingIdsRef.current.add(String(transformedMessage.id))
          }

          return enrichMessagesWithReplies(updated)
        })

        // Scroll to bottom
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (containerRef.current) {
              containerRef.current.scrollTo({
                top: containerRef.current.scrollHeight,
                behavior: 'smooth'
              })
            }
          })
        })
      }

      // Cleanup: Revoke preview URLs after upload
      if (attachmentsToUpload.length) {
        attachmentsToUpload.forEach((a) => {
          if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
        })
      }
    } catch (err) {
      // Remove optimistic message on error
      setChats((prev) => prev.filter((msg) => msg.id !== tempId))
    } finally {
      setIsUploadingImage(false)
      // Clear preview state after send
      setInputPreviewUrl(null)
      setInputPreviewMetadata(null)
      // Clear sending flag after delay to allow realtime events from our send to be ignored
      setTimeout(() => {
        isSendingMessageRef.current = false
      }, 2500)
    }

    // Reset textarea height
    const textAreaRef = inputRef.current

    if (textAreaRef) {
      textAreaRef.style.height = ''
      textAreaRef.style.overflowY = 'hidden'
    }
  }, [currentChatId, userDetails, value, repliedMessage, enrichMessagesWithReplies, attachments, uploadToSupabaseStorage, inputPreviewMetadata, inputPreviewUrl])

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const handleChange = (newValue) => {
    setValue(newValue)

    const textAreaRef = inputRef.current

    if (textAreaRef) {
      textAreaRef.style.height = 'auto'
      const CAP = 200
      const newHeight = Math.min(textAreaRef.scrollHeight, CAP)

      textAreaRef.style.height = `${newHeight}px`
      textAreaRef.style.overflowY = textAreaRef.scrollHeight > CAP ? 'auto' : 'hidden'
    }
  }

  // Handle search
  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue)
    if (!searchValue.trim()) {
      setFilteredPersonalChats(personalChats)
    } else {
      const filtered = personalChats.filter((chat) => {
        // Use API response field names
        const name = (chat.other_user_name || `${chat.first_name || ''} ${chat.last_name || ''}`.trim() || '').toLowerCase()

        return name.includes(searchValue.toLowerCase())
      })

      setFilteredPersonalChats(filtered)
    }
  }

  // Fetch chat list on mount
  useEffect(() => {
    fetchPersonalChatList(true) // Skip debounce on initial load
  }, [fetchPersonalChatList])

  // Fetch communities on mount
  useEffect(() => {
    fetchCommunities()
  }, [])

  // Cleanup debounce timer on unmount
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      if (chatListDebounceTimerRef.current) {
        clearTimeout(chatListDebounceTimerRef.current)
      }
    }
  }, [])

  // Notify parent when sidebar content is ready (for external rendering)
  useEffect(() => {
    if (onSidebarReady && hideSidebar) {
      const sidebarContent = {
        searchTerm,
        handleSearch,
        isLoading,
        filteredPersonalChats,
        selectedUserId: selectedUserId || internalSelectedUserId,
        handleUserSelect,
        userDetails,
        handleLogin,
        UserRow,
        t
      }

      onSidebarReady(sidebarContent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSidebarReady, hideSidebar, searchTerm, isLoading, filteredPersonalChats, selectedUserId, internalSelectedUserId])

  // Real-time subscription for updating chat list when messages arrive in any chat
  useEffect(() => {
    if (!userDetails?.id) return

    const channel = supabase
      .channel('personal-chat-list-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'personal_messages'
        },
        (payload) => {
          // Skip when current user sent the message
          if (String(payload?.new?.sender_id) === String(userDetails?.id)) {
            return
          }
          // Refresh the chat list to update last message and unread count
          // This will update the sidebar even if the chat is not currently open
          // Use debouncing to batch multiple rapid updates
          fetchPersonalChatList()
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'personal_chats',
          filter: `user1_id=eq.${userDetails.id}`
        },
        () => {
          // Skip refresh if we're updating unread count ourselves (during initial load) to prevent duplicate calls
          if (isUpdatingUnreadCountRef.current) {
            return
          }
          // Skip refresh if we're currently loading a chat (initial load) to prevent duplicate calls
          // Check if we're processing a user selection or fetching messages
          if (isInitialLoadRef.current || isFetchingRef.current || processingUserIdRef.current) {
            return
          }
          // Refresh chat list when unread counts or last_read timestamps are updated for user1
          // Use debouncing to batch multiple rapid updates
          fetchPersonalChatList()
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'personal_chats',
          filter: `user2_id=eq.${userDetails.id}`
        },
        () => {
          // Skip refresh if we're updating unread count ourselves (during initial load) to prevent duplicate calls
          if (isUpdatingUnreadCountRef.current) {
            return
          }
          // Skip refresh if we're currently loading a chat (initial load) to prevent duplicate calls
          // Check if we're processing a user selection or fetching messages
          if (isInitialLoadRef.current || isFetchingRef.current || processingUserIdRef.current) {
            return
          }
          // Refresh chat list when unread counts or last_read timestamps are updated for user2
          // Use debouncing to batch multiple rapid updates
          fetchPersonalChatList()
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // console.log('Realtime subscribed for personal chat list updates')
        }
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userDetails?.id, fetchPersonalChatList])

  // Handle selectedUserId changes (from parent)
  useEffect(() => {
    if (selectedUserId && userDetails?.id) {
      // Only process if it's different from what we're currently processing or already loaded
      const isAlreadyProcessing = processingUserIdRef.current === selectedUserId
      const isAlreadyLoaded = internalSelectedUserId === selectedUserId && currentChatId && !isFetchingRef.current

      if (!isAlreadyProcessing && !isAlreadyLoaded) {
        // Proceed with user selection - handleUserSelect can handle users not in the list
        setInternalSelectedUserId(selectedUserId)
        handleUserSelect(selectedUserId)
      }
    } else if (!selectedUserId) {
      // Reset when selectedUserId is cleared
      processingUserIdRef.current = null
      fetchingChatIdRef.current = null
      setInternalSelectedUserId(null)
      setSelectedChatUser(null)
      setCurrentChatId(null)
      setChats([])
      setAttachments([])
      setIsFetchingOlder(false) // Clear loading state
      setIsUserBlocked(false) // Reset blocked status
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, userDetails?.id])

  // Scroll handling for pagination
  useEffect(() => {
    const container = containerRef.current

    if (!container) return

    const handleScroll = () => {
      if (container.scrollTop <= 60 && !isFetchingRef.current && hasMoreRef.current && currentChatId) {
        fetchPersonalMessages(currentChatId, false)
      }
    }

    container.addEventListener('scroll', handleScroll)

    return () => container.removeEventListener('scroll', handleScroll)
  }, [currentChatId, fetchPersonalMessages])

  // Reset prevChatsLengthRef and prevLastMessageIdRef when chat changes
  useEffect(() => {
    prevChatsLengthRef.current = 0
    prevLastMessageIdRef.current = null
  }, [currentChatId])

  // Auto-scroll to bottom when new messages arrive (only if added at bottom, not when loading older messages)
  useEffect(() => {
    const el = containerRef.current

    // Don't auto-scroll if:
    // 1. Container doesn't exist
    // 2. User is not at bottom
    // 3. We're currently fetching older messages
    // 4. This is initial load (prevChatsLengthRef is 0) - initial load handles scrolling manually
    if (!el || !atBottomRef.current || isFetchingOlderRef.current || prevChatsLengthRef.current === 0) {
      // Update refs even if we don't scroll
      if (chats.length > 0) {
        prevChatsLengthRef.current = chats.length
        prevLastMessageIdRef.current = chats[chats.length - 1]?.id || null
      }

      return
    }

    // Check if messages were added at the bottom by comparing last message ID
    // If last message ID changed, messages were added at bottom
    // If last message ID is same but length increased, messages were added at top (older messages)
    const currentLastMessageId = chats.length > 0 ? chats[chats.length - 1]?.id : null
    const lastMessageChanged = currentLastMessageId && currentLastMessageId !== prevLastMessageIdRef.current
    const messagesAdded = chats.length > prevChatsLengthRef.current

    // Only auto-scroll if messages were added at the bottom (last message ID changed)
    if (messagesAdded && lastMessageChanged) {
      // Verify we're still at bottom before auto-scrolling
      const distance = el.scrollHeight - (el.scrollTop + el.clientHeight)
      const isAtBottom = distance < BOTTOM_EPS

      if (isAtBottom) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
          })
        })
      }
    }

    // Update previous refs after checking
    prevChatsLengthRef.current = chats.length
    prevLastMessageIdRef.current = currentLastMessageId
  }, [chats.length, chats])

  // Track scroll position
  useEffect(() => {
    const el = containerRef.current

    if (!el) return

    const onScroll = () => {
      const distance = el.scrollHeight - (el.scrollTop + el.clientHeight)
      const isAtBottom = distance < BOTTOM_EPS

      atBottomRef.current = isAtBottom
    }

    el.addEventListener('scroll', onScroll)
    onScroll() // Initialize

    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  // Real-time subscription for personal chat messages
  useEffect(() => {
    if (!currentChatId || !userDetails?.id) return

    const channel = supabase
      .channel(`personal-chat:${currentChatId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'personal_messages',
          filter: `chat_id=eq.${currentChatId}`
        },
        async (payload) => {
          const newMessage = payload.new

          // Fetch user info
          const { data: user } = await supabase.from('users').select('userid, first_name, last_name, users_flag_url').eq('userid', newMessage.sender_id).single()

          // Fetch attachments if any
          let attachments = []

          if (newMessage.has_attachments) {
            await new Promise((resolve) => {
              setTimeout(() => {
                resolve()
              }, 100)
            })
            const { data: attachmentsData } = await supabase.from('personal_message_attachments').select('*').eq('message_id', newMessage.id)

            if (attachmentsData) {
              attachments = attachmentsData
            }
          }

          // Fetch URL preview if message has URL but no preview metadata
          let urlPreview = null

          if (newMessage.content) {
            const hasUrl = urlMethods.hasUrlInMessage(newMessage.content, attachments)
            const firstUrl = urlMethods.getFirstUrl(newMessage.content)

            if (hasUrl && !newMessage.url_preview && firstUrl) {
              try {
                const response = await getURLMetadata(firstUrl, null)
                const data = response?.data || response

                if (data && typeof data === 'object' && !Array.isArray(data) && (data.title || data.description || data.image)) {
                  const domain = urlMethods.extractDomainFromUrl(firstUrl)
                  const title = data.title && data.title !== firstUrl ? data.title : domain

                  urlPreview = {
                    title: title || domain,
                    description: data.description || '',
                    image: data.image || '',
                    url: firstUrl,
                    siteName: data.siteName || domain
                  }
                }
              } catch (err) {
                // console.error('Error fetching URL preview for real-time message:', err)
              }
            } else if (newMessage.url_preview) {
              urlPreview = newMessage.url_preview
            }
          }

          const fullMessage = {
            ...newMessage,
            userid: newMessage.sender_id, // Add userid for ChatMessage compatibility
            user_info: user || {
              userid: newMessage.sender_id,
              first_name: '',
              last_name: '',
              users_flag_url: ''
            },
            attachments,
            url_preview: urlPreview || newMessage.url_preview || null,
            pending: false
          }

          // Check if message already exists - prevent duplicates
          setChats((prev) => {
            // First check if message already exists in current state by ID
            const existingIndex = prev.findIndex((m) => String(m.id) === String(fullMessage.id))

            if (existingIndex >= 0) {
              // Message already exists, update it with latest data (in case of edits or attachment updates)
              const updatedChats = [...prev]

              updatedChats[existingIndex] = { ...updatedChats[existingIndex], ...fullMessage }

              return enrichMessagesWithReplies(updatedChats)
            }

            // If this is our own message, check if it was already added via manual fetch
            // (This prevents duplicates when both manual fetch and real-time subscription try to add the same message)
            if (fullMessage.sender_id === userDetails.id) {
              // Check if message ID is already in existingIdsRef (added by manual fetch)
              if (existingIdsRef.current.has(String(fullMessage.id))) {
                // Message was already added manually, don't add again
                return prev
              }

              // Find optimistic message by matching content and timestamp (within 5 seconds)
              const optimisticIndex = prev.findIndex((m) => {
                if (!m.pending) return false
                if (m.chat_id !== fullMessage.chat_id) return false
                if (m.sender_id !== fullMessage.sender_id) return false

                // Match by content or timestamp (within 5 seconds)
                const timeDiff = Math.abs(new Date(m.timestamp).getTime() - new Date(fullMessage.timestamp).getTime())
                const contentMatch = m.content === fullMessage.content || (!m.content && !fullMessage.content)

                return contentMatch && timeDiff < 5000
              })

              if (optimisticIndex >= 0) {
                const newChats = [...prev]

                newChats[optimisticIndex] = { ...fullMessage, pending: false }
                existingIdsRef.current.add(String(fullMessage.id))
                // Sort chronologically after replacing optimistic message
                const sortedChats = newChats.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))

                return enrichMessagesWithReplies(sortedChats)
              }
            }

            // Add new incoming message (from other users or if no optimistic message found)
            // Add to existingIdsRef to prevent future duplicates
            existingIdsRef.current.add(String(fullMessage.id))

            // Add message and sort chronologically to maintain order
            const updatedChats = [...prev, fullMessage]
            const sortedChats = updatedChats.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))

            return enrichMessagesWithReplies(sortedChats)
          })

          // If user is viewing this chat, update unread count since they've seen the message
          if (currentChatId && fullMessage.chat_id === currentChatId) {
            await updatePersonalChatUnreadCount(currentChatId)
            // Don't call fetchPersonalChatList here - updatePersonalChatUnreadCount already calls it
          } else {
            // Only refresh chat list if this is not the current chat (to avoid duplicate calls)
            // The general subscription will handle updates for all chats
            fetchPersonalChatList()
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'personal_messages',
          filter: `chat_id=eq.${currentChatId}`
        },
        (payload) => {
          const updatedMessage = payload.new

          // Check if message should be hidden based on deleted flags
          const isSender = updatedMessage.sender_id === userDetails.id

          if ((isSender && updatedMessage.deleted_for_sender) || (!isSender && updatedMessage.deleted_for_receiver)) {
            setChats((prev) => prev.filter((msg) => msg.id !== updatedMessage.id))

            return
          }

          // Transform updated message to match expected structure
          const transformedMessage = {
            ...updatedMessage,
            userid: updatedMessage.sender_id,
            user_info: updatedMessage.sender_info || {
              userid: updatedMessage.sender_id,
              first_name: '',
              last_name: '',
              users_flag_url: ''
            }
          }

          setChats((prev) => enrichMessagesWithReplies(prev.map((msg) => (msg.id === updatedMessage.id ? { ...msg, ...transformedMessage } : msg))))
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'personal_messages',
          filter: `chat_id=eq.${currentChatId}`
        },
        (payload) => {
          const deletedId = payload.old?.id

          if (deletedId) {
            setChats((prev) => prev.filter((msg) => msg.id !== deletedId))
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // console.log('Realtime subscribed for personal chat', currentChatId)
        }
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [currentChatId, userDetails?.id, fetchPersonalChatList, updatePersonalChatUnreadCount, enrichMessagesWithReplies])

  // UserRow component
  const UserRow = ({ userData, isActive, onClick }) => {
    // Use API response field names: other_user_id, other_user_name, other_user_avatar_url
    const userId = userData.other_user_id || userData.user_id || userData.id
    const userName = userData.other_user_name || userData.first_name || userData.name || 'User'
    const avatarUrl = userData.other_user_avatar_url || userData.users_flag_url || userData.avatar

    return (
      <div className={`personal-chat-item ${isActive ? 'active' : ''}`} onClick={onClick}>
        <div className="personal-chat-avatar">{avatarUrl ? <img src={avatarUrl} alt="user-avatar" loading="lazy" decoding="async" draggable={false} /> : <div className="avatar-text">{userName[0] || 'U'}</div>}</div>
        <div className="personal-chat-info">
          <div className="personal-chat-name">{userName}</div>
          {userData.last_message && <div className="personal-chat-preview">{userData.last_message}</div>}
        </div>
        {userData.unread_count > 0 && <div className="unread-count-div">{userData.unread_count > 99 ? '99+' : userData.unread_count}</div>}
      </div>
    )
  }

  const handleDeleteForMe = useCallback(
    async (msg) => {
      if (!msg?.id || !userDetails?.id) return

      await supabase.rpc('delete_personal_message_for_me', { p_message_id: msg?.id, p_user_id: userDetails?.id })
      setChats((prev) => prev.filter((m) => m.id !== msg.id))
    },
    [userDetails?.id]
  )

  const handleDeleteForEveryone = useCallback(
    async (msg) => {
      if (!msg?.id || !userDetails?.id) return

      const senderId = msg?.sender_id || msg?.userid

      await supabase.rpc('delete_personal_message_for_all', {
        p_message_id: msg.id,
        p_sender_id: senderId
      })

      setChats((prev) => prev.filter((m) => m.id !== msg.id))
    },
    [userDetails?.id]
  )

  // Handle edit modal
  const openEditModal = (msg) => {
    if (!userDetails?.id) {
      setShowRedirectText(true)

      return
    }

    setMessageToEdit(msg)
    setEditModalVisible(true)
  }

  const handleEditSave = async (editValue) => {
    if (!editValue || !messageToEdit) return

    // Update personal message
    const updatePayload = {
      content: editValue?.trim(),
      edited_at: new Date().toISOString()
    }

    const captionUpdatePayload = {
      caption: editValue?.trim()
    }

    if (messageToEdit.attachments.length > 0) {
      await Promise.all([supabase.from('personal_message_attachments').update({ caption: captionUpdatePayload.caption }).eq('id', messageToEdit?.attachments[0]?.id), supabase.from('personal_messages').update({ edited_at: updatePayload.edited_at }).eq('id', messageToEdit?.id)])
      // Update local state with all changes
      setChats((prev) =>
        prev.map((msg) => {
          if (msg?.id !== messageToEdit?.id) return msg

          return {
            ...msg,
            edited_at: updatePayload.edited_at,
            attachments: (msg.attachments || []).map((att) => {
              if (att.id === messageToEdit.attachments[0].id) {
                return {
                  ...att,
                  caption: editValue
                }
              }

              return att
            })
          }
        })
      )
    } else {
      await supabase.from('personal_messages').update(updatePayload).eq('id', messageToEdit.id)

      // Update local state with all changes
      setChats((prev) => prev.map((msg) => (msg.id === messageToEdit.id ? { ...msg, ...updatePayload } : msg)))
    }

    setEditModalVisible(false)
    setMessageToEdit(null)
  }

  const handleClearChat = useCallback(async () => {
    if (!currentChatId || !userDetails?.id) return

    try {
      const { error } = await supabase.rpc('clear_personal_chat_for_me', {
        p_chat_id: currentChatId,
        p_user_id: userDetails.id
      })

      if (error) {
        return
      }

      // Clear messages from state
      setChats([])

      // Refresh chat list to update the UI
      await fetchPersonalChatList(true)
    } catch (err) {
      // console.error('Error clearing chat:', err)
    }
  }, [currentChatId, userDetails?.id, fetchPersonalChatList])

  const handleBlockUser = useCallback(async () => {
    if (!selectedChatUser?.other_user_id || !userDetails?.id) return

    try {
      const { error } = await supabase.rpc('block_personal_chat_user', {
        p_blocker_id: userDetails.id,
        p_blocked_user_id: selectedChatUser.other_user_id
      })

      if (error) {
        return
      }

      // Update blocked status
      setIsUserBlocked(true)

      // Refresh chat list to update the UI
      await fetchPersonalChatList(true)
    } catch (err) {
      // console.error('Error blocking user:', err)
    }
  }, [selectedChatUser?.other_user_id, userDetails?.id, fetchPersonalChatList])

  const handleUnblockUser = useCallback(async () => {
    if (!selectedChatUser?.other_user_id || !userDetails?.id) return

    try {
      const { error } = await supabase.rpc('unblock_personal_chat_user', {
        p_blocker_id: userDetails.id,
        p_blocked_user_id: selectedChatUser.other_user_id
      })

      if (error) {
        return
      }

      // Update blocked status
      setIsUserBlocked(false)

      // Refresh chat list to update the UI
      await fetchPersonalChatList(true)
    } catch (err) {
      // console.error('Error unblocking user:', err)
    }
  }, [selectedChatUser?.other_user_id, userDetails?.id, fetchPersonalChatList])
  // Handle confirmation modal (for delete actions)
  const openConfirmationModal = (action, msg) => {
    if (!userDetails?.id) {
      setShowRedirectText(true)

      return
    }

    setModalAction(action)
    setModalMessage(msg)
    setConfirmationModal(true)
  }

  const handleModalConfirm = async () => {
    if (modalAction === 'deleteForMe') {
      await handleDeleteForMe(modalMessage)
    } else if (modalAction === 'deleteForEveryone') {
      await handleDeleteForEveryone(modalMessage)
    } else if (modalAction === 'clearChat') {
      await handleClearChat()
    } else if (modalAction === 'blockUser') {
      await handleBlockUser()
    } else if (modalAction === 'unblockUser') {
      await handleUnblockUser()
    }
    setConfirmationModal(false)
    setModalAction(null)
    setModalMessage(null)
  }

  const confirmationModalContent = () => {
    if (!modalMessage) return null

    let confirmationText = ''

    if (modalAction === 'deleteForMe') {
      confirmationText = 'Do you wish to delete this message for yourself? This action cannot be undone.'
    } else if (modalAction === 'deleteForEveryone') {
      confirmationText = 'Do you wish to delete this message for everyone? This action cannot be undone.'
    } else if (modalAction === 'clearChat') {
      confirmationText = 'Do you wish to delete this conversation'
    } else if (modalAction === 'blockUser') {
      confirmationText = 'Do you wish to block this user'
    } else if (modalAction === 'unblockUser') {
      confirmationText = 'Do you wish to unblock this user'
    }

    return (
      <div>
        <div className="message-preview">{confirmationText}</div>
      </div>
    )
  }

  const footerContent = () => (
    <div className="confirmation-modal-buttons">
      <Button label="No" className="confirmation-no-button" onClick={() => setConfirmationModal(false)} />
      <Button label="Yes" className="confirmation-yes-button" onClick={handleModalConfirm} />
    </div>
  )

  // Handle reply
  const handleReply = (msg) => {
    setRepliedMessage(msg)
    if (inputRef.current) {
      inputRef.current.focus()
    }
    if (repliedMsgRef) {
      repliedMsgRef.current = msg
    }
  }

  // Handle forward
  const openForwardModal = (msg) => {
    if (!msg) return
    setMessageToForward(msg)
    setForwardModalVisible(true)
  }

  const handleForwardToUsers = async (users) => {
    if (!users || users.length === 0) return
    if (!messageToForward) return
    if (!userDetails?.id) {
      setShowRedirectText(true)

      return
    }

    setIsForwarding(true)

    try {
      const forwardPromises = users.map(async (user) => {
        const userId = user.other_user_id || user.user_id || user.id

        if (!userId) return

        // Check permission and get/create chat_id
        const chatData = await checkPermissionAndInitiateChat(userId)

        if (!chatData) {
          throw new Error(`Failed to initiate chat with user ${userId}`)
        }

        const chatId = chatData.chat_id || chatData.id

        if (!chatId) {
          throw new Error(`No chat_id found for user ${userId}`)
        }

        // Prepare attachments payload for personal chat
        const attachmentsPayload = (messageToForward.attachments || []).map((att, idx) => {
          // Personal chat attachments use attachment_url
          const attachmentUrl = att.attachment_url || att.media_url || att.url || ''

          // Use helper function to determine attachment type
          const attachmentType = getAttachmentType({
            attachment_type: att.attachment_type,
            mime_type: att.mime_type || att.mime,
            file_name: att.file_name || att.name
          })

          return {
            attachment_type: attachmentType,
            attachment_url: attachmentUrl,
            thumbnail_url: att.thumbnail_url || '',
            file_name: att.file_name || att.name || '',
            file_size: att.file_size || att.size || 0,
            caption: att.caption || ''
          }
        })

        // Forward message to personal chat
        return supabase.rpc('create_personal_message_with_attachments', {
          p_chat_id: chatId,
          p_sender_id: userDetails.id,
          p_content: messageToForward.content || '',
          p_message_type: attachmentsPayload.length ? 'file' : 'text',
          p_reply_to_id: null,
          p_attachments: attachmentsPayload
        })
      })

      await Promise.all(forwardPromises)

      setForwardModalVisible(false)
      setMessageToForward(null)
    } catch (err) {
      // console.error('Forward failed', err)
    } finally {
      setIsForwarding(false)
    }
  }

  // Fetch communities for forwarding
  const fetchCommunities = useCallback(async () => {
    if (!userDetails?.id) {
      setCommunities([])

      return
    }

    try {
      // Check if user is admin
      const { data: adminData } = await supabase.from('users').select('can_act_as_bot').eq('userid', userDetails.id).single()

      setIsAdminUser(!!adminData?.can_act_as_bot)

      // Fetch communities
      const groupListResponse = await supabase.rpc('get_user_communities', { p_userid: userDetails.id })
      const internalGroupList = await supabase.rpc('get_internal_communities', { p_userid: userDetails.id || 0 })

      const combinedGroupList = [...(groupListResponse.data || []), ...(internalGroupList.data || [])]

      // Separate by sort_order
      const sortOrderZero = combinedGroupList.filter((g) => !g.sort_order || g.sort_order === 0)
      const sortOrderNonZero = combinedGroupList.filter((g) => g.sort_order && g.sort_order > 0).sort((a, b) => a.sort_order - b.sort_order)

      // Insert sortOrderNonZero at their index
      const finalList = [...sortOrderZero]

      sortOrderNonZero.forEach((g) => {
        const idx = Math.max(0, Math.min(g.sort_order, finalList.length))

        finalList.splice(idx, 0, g)
      })

      setCommunities(finalList)
    } catch (err) {
      setCommunities([])
    }
  }, [userDetails?.id])

  // Handle forwarding to communities
  const handleForwardToCommunities = async (selectedCommunities) => {
    if (!selectedCommunities || selectedCommunities.length === 0) return
    if (!messageToForward) return
    if (!userDetails?.id) {
      setShowRedirectText(true)

      return
    }

    setIsForwarding(true)

    try {
      const forwardPromises = selectedCommunities.map((community) => {
        // Prepare attachments payload for community messages
        const attachmentsPayload = (messageToForward.attachments || []).map((att, idx) => {
          const attachmentUrl = att.attachment_url || att.media_url || att.url || ''
          const mimeType = att.mime_type || att.mime || ''

          // Use helper function to determine attachment type
          const attachmentType = getAttachmentType({
            attachment_type: att.attachment_type,
            mime_type: mimeType,
            file_name: att.file_name || att.name
          })

          return {
            caption: att.caption || '',
            sort_order: idx,
            s3_key: att.s3_key || '',
            s3_bucket: att.s3_bucket || '',
            file_name: att.file_name || att.name || '',
            file_size: att.file_size || att.size || 0,
            mime_type: mimeType,
            media_url: attachmentUrl,
            thumbnail_url: att.thumbnail_url || '',
            metadata: att.metadata || {}
          }
        })

        return supabase.rpc('create_message_with_attachments', {
          p_community_id: community.id,
          p_user_id: userDetails.id,
          p_content: messageToForward.content || '',
          p_message_type: messageToForward.type || (attachmentsPayload.length ? 'file' : 'text'),
          p_reply_to_id: null,
          p_attachments: attachmentsPayload,
          p_is_forwarded: true
        })
      })

      await Promise.all(forwardPromises)

      setForwardModalVisible(false)
      setMessageToForward(null)
    } catch (err) {
      // console.error('Forward to communities failed', err)
    } finally {
      setIsForwarding(false)
    }
  }

  // Scroll to message
  const scrollToMessage = (messageId) => {
    if (!containerRef.current || !messageId) return
    const messageElement = document.getElementById(`msg-${messageId}`)

    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      messageElement.classList.add('highlight-message')
      setTimeout(() => {
        messageElement.classList.remove('highlight-message')
      }, 2000)
    }
  }

  // Open attachment viewer
  const openViewer = (msg, att, index) => {
    // Use attachment_url first (from database), fallback to media_url or url
    const attachmentUrl = att.attachment_url || att.media_url || att.url || ''
    // Use mime_type or mime for type, fallback to attachment_type converted to mime format
    let attachmentType = att.mime_type || att.mime || ''

    // If no mime_type but we have attachment_type, convert it
    if (!attachmentType && att.attachment_type) {
      if (att.attachment_type === 'image') {
        attachmentType = 'image/jpeg' // Default to jpeg if we only know it's an image
      } else if (att.attachment_type === 'video') {
        attachmentType = 'video/mp4' // Default to mp4 if we only know it's a video
      } else if (att.attachment_type === 'document') {
        attachmentType = 'application/pdf' // Default to pdf for documents
      }
    }

    setViewer({
      open: true,
      type: attachmentType,
      url: attachmentUrl,
      name: `${userDetails?.id === msg?.userid || userDetails?.id === msg?.sender_id ? 'You' : `${msg?.user_info?.first_name || ''} ${msg?.user_info?.last_name || ''}`.trim() || 'User'}`,
      attachments: msg.attachments || [],
      currentIndex: index
    })
  }

  // Close attachment viewer
  const closeViewer = useCallback(() => {
    setViewer({
      open: false,
      type: null,
      url: '',
      name: '',
      attachments: [],
      currentIndex: 0
    })
    setIsFullscreen(false) // Reset fullscreen when closing
  }, [])

  // Navigate between attachments
  const navigateAttachment = useCallback(
    (direction) => {
      const { attachments, currentIndex } = viewer

      if (!attachments || attachments.length === 0) return

      let newIndex

      if (direction === 'prev') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : attachments.length - 1
      } else {
        newIndex = currentIndex < attachments.length - 1 ? currentIndex + 1 : 0
      }

      const newAtt = attachments[newIndex]
      // Use attachment_url first (from database), fallback to media_url or url
      const attachmentUrl = newAtt.attachment_url || newAtt.media_url || newAtt.url || ''
      // Use mime_type or mime for type, fallback to attachment_type converted to mime format
      let attachmentType = newAtt.mime_type || newAtt.mime || ''

      // If no mime_type but we have attachment_type, convert it
      if (!attachmentType && newAtt.attachment_type) {
        if (newAtt.attachment_type === 'image') {
          attachmentType = 'image/jpeg' // Default to jpeg if we only know it's an image
        } else if (newAtt.attachment_type === 'video') {
          attachmentType = 'video/mp4' // Default to mp4 if we only know it's a video
        } else if (newAtt.attachment_type === 'document') {
          attachmentType = 'application/pdf' // Default to pdf for documents
        }
      }

      // If still no mime type, use getAttachmentType helper to infer from file extension
      if (!attachmentType) {
        const inferredType = getAttachmentType(newAtt)

        if (inferredType === 'image') {
          attachmentType = 'image/jpeg' // Default to jpeg if inferred as image
        } else if (inferredType === 'video') {
          attachmentType = 'video/mp4' // Default to mp4 if inferred as video
        } else if (inferredType === 'document') {
          attachmentType = 'application/pdf' // Default to pdf for documents
        }
      }

      setViewer({
        ...viewer,
        url: attachmentUrl,
        type: attachmentType,
        currentIndex: newIndex
      })
    },
    [viewer, getAttachmentType]
  )

  // Keyboard navigation for viewer
  useEffect(() => {
    if (!viewer.open) return

    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        navigateAttachment('prev')
      } else if (e.key === 'ArrowRight') {
        navigateAttachment('next')
      } else if (e.key === 'Escape') {
        closeViewer()
      }
    }

    window.addEventListener('keydown', handleKeyPress)

    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [viewer.open, viewer.currentIndex, navigateAttachment, closeViewer])

  const viewerModalContent = () => {
    if (!viewer.url) return null

    // Get current attachment to check attachment_type as fallback
    const currentAtt = viewer.attachments?.[viewer.currentIndex]

    const attachmentType = currentAtt?.attachment_type || ''

    // Check if image: mime_type starts with 'image' OR attachment_type is 'image'
    const isImage = viewer.type?.startsWith('image') || attachmentType === 'image'
    // Check if video: mime_type starts with 'video' OR attachment_type is 'video'
    const isVideo = viewer.type?.startsWith('video') || attachmentType === 'video'
    const isPdf = viewer.type === 'application/pdf' || viewer.name?.toLowerCase().endsWith('.pdf')
    // const isDoc = viewer.type?.includes('document') || attachmentType === 'document' || /\.(docx|doc|xls|xlsx|csv|txt)$/i.test(viewer.name)

    const hasMultipleImages =
      viewer.attachments &&
      viewer.attachments.length > 1 &&
      viewer.attachments.every((att) => {
        const attType = att.mime_type || att.mime || ''
        const attAttachmentType = att.attachment_type || ''

        return attType?.startsWith('image') || attAttachmentType === 'image'
      })

    const hasMultipleVideos =
      viewer.attachments &&
      viewer.attachments.length > 1 &&
      viewer.attachments.every((att) => {
        const attType = att.mime_type || att.mime || ''
        const attAttachmentType = att.attachment_type || ''

        return attType?.startsWith('video') || attAttachmentType === 'video'
      })

    // Check if there are multiple media attachments (images or videos mixed together)
    const hasMultipleMedia =
      viewer.attachments &&
      viewer.attachments.length > 1 &&
      viewer.attachments.every((att) => {
        const attType = att.mime_type || att.mime || ''
        const attAttachmentType = att.attachment_type || ''

        return attType?.startsWith('image') || attAttachmentType === 'image' || attType?.startsWith('video') || attAttachmentType === 'video'
      })

    // Show navigation for:
    // 1. Multiple images (when viewing an image)
    // 2. Multiple videos (when viewing a video)
    // 3. Multiple media (images + videos mixed) when viewing any media
    const showNavigation = (hasMultipleImages && isImage) || (hasMultipleVideos && isVideo) || (hasMultipleMedia && (isImage || isVideo))

    return (
      <div className={`attachment-viewer-content ${isFullscreen ? 'fullscreen' : ''}`}>
        <div className="attachment-viewer-wrapper">
          {isImage && (
            <div ref={zoomContainerRef} className="attachment-zoom-container" onWheel={handleWheelZoom} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp} style={{ position: 'relative', overflow: 'hidden', touchAction: 'none', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={viewer.url}
                alt={viewer.name}
                ref={imgRef}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: getTransformStyle(),
                  transformOrigin: 'center center',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  cursor: zoomLevel > 1 ? 'move' : 'default'
                }}
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
              />
            </div>
          )}
          {isVideo && <video src={viewer.url} controls autoPlay={true} style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }} controlsList="nodownload noplaybackrate" disablePictureInPicture onContextMenu={(e) => e.preventDefault()} />}
          {isPdf && <iframe src={viewer.url} title={viewer.name} style={{ width: '90%', height: '100%', border: 'none', margin: 'auto' }} onContextMenu={(e) => e.preventDefault()} />}
          {/* {isDoc && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '20px' }}>
              <i className="pi pi-file" style={{ fontSize: '64px', color: '#666', marginBottom: '16px' }} />
              <p style={{ margin: 0, color: '#666' }}>Document preview not available</p>
              <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#999' }}>{viewer.name}</p>
            </div>
          )} */}
        </div>
        {/* {viewer.attachments.length > 0 && <div className="attachment-viewer-caption">{viewer.attachments[viewer.currentIndex]?.caption}</div>} */}
        {/* Navigation Arrows */}
        {showNavigation && (
          <>
            <button className="nav-arrow nav-arrow-left" onClick={() => navigateAttachment('prev')} aria-label={isVideo ? 'Previous video' : 'Previous image'}>
              <i className="pi pi-chevron-left" />
            </button>
            <button className="nav-arrow nav-arrow-right" onClick={() => navigateAttachment('next')} aria-label={isVideo ? 'Next video' : 'Next image'}>
              <i className="pi pi-chevron-right" />
            </button>
            <div className="image-counter">
              {viewer.currentIndex + 1} / {viewer.attachments.length}
            </div>
          </>
        )}
      </div>
    )
  }

  const handleLogin = () => {
    if (userLoginStatus === false) {
      history.push({ pathname: ROUTE_STRINGS.login, state: { coffeePostPersonalChat: true } })
    } else {
      history.push(ROUTE_STRINGS.createAccount)
    }
  }

  return (
    <div className={`personal-chat-wrapper ${isCoffeePost ? 'coffee-post-screen' : ''}`}>
      {/* Left: personal chat list sidebar */}
      {!hideSidebar && (
        <aside className={`personal-chat-sidebar ${isCoffeePost && showChatInCoffeePost ? 'hidden' : ''} ${isCoffeePost ? 'coffee-post-screen-personal-chat-sidebar' : ''}`}>
          {!isCoffeePost && <div className="component-label">{t('PERSONAL_CHATS')}</div>}
          {userDetails && (
            <div className="search-input-div">
              <InputText value={searchTerm} onChange={(e) => handleSearch(e.target.value)} placeholder="Search chats" className="search-input" />
              {searchTerm ? (
                <button type="button" className="search-btn" onClick={() => handleSearch('')}>
                  <i className="pi pi-times" />
                </button>
              ) : (
                <button type="button" className="search-btn" aria-controls="search-input">
                  <i className="pi pi-search" />
                </button>
              )}
            </div>
          )}
          <div className={`personal-chat-items-container ${!userDetails ? 'guest-chat-items-container' : ''} ${isCoffeePost ? 'coffee-post-screen-personal-chats' : ''}`}>
            {(() => {
              if (isLoading) {
                return (
                  <>
                    {Array(8)
                      .fill(null)
                      .map((_, index) => (
                        <div key={index} className="personal-chat-skeleton">
                          <Skeleton shape="circle" height="40px" width="45px" className="custom-skelton" />
                          <div className="body-skeleton-wrapper">
                            <Skeleton height="2rem" className="custom-skelton" />
                            <Skeleton shape="circle" size="1rem" className="custom-skelton" />
                          </div>
                        </div>
                      ))}
                  </>
                )
              }

              if (filteredPersonalChats.length > 0) {
                return filteredPersonalChats.map((chat) => {
                  const userId = chat.other_user_id || chat.user_id || chat.id
                  const activeUserId = selectedUserId || internalSelectedUserId

                  return <UserRow key={userId || chat.chat_id} userData={chat} isActive={String(userId) === String(activeUserId)} onClick={() => handleUserSelect(userId)} />
                })
              }

              if (!userDetails?.id && isCoffeePost) {
                return (
                  <div className="no-chats-message guest-message">
                    <div className="no-chats-icon-wrapper">
                      <i className="pi pi-user-plus no-chats-icon" />
                    </div>
                    <div className="no-chats-content">
                      <div className="no-chats-title">{t('START_A_PERSONAL_CHAT')}</div>
                      <p className="no-chats-text">
                        Please <span onClick={handleLogin}>{t('LOGIN_AND_CREATE_ACCOUNT')}</span> to chat personally.
                      </p>
                    </div>
                  </div>
                )
              }

              return (
                <div className="no-chats-message logged-in-message">
                  <div className="no-chats-icon-wrapper">
                    <i className="pi pi-comments no-chats-icon" />
                  </div>
                  <div className="no-chats-content">
                    <div className="no-chats-title">{t('NO_PERSONAL_CHATS_FOUND')}</div>
                    <p className="no-chats-text">{t('CONVERSATION_MESSAGE')}</p>
                  </div>
                </div>
              )
            })()}
          </div>
        </aside>
      )}

      {/* Render sidebar via render prop if provided */}
      {renderSidebar &&
        renderSidebar({
          searchTerm,
          handleSearch,
          isLoading,
          filteredPersonalChats,
          selectedUserId: selectedUserId || internalSelectedUserId,
          handleUserSelect,
          userDetails,
          handleLogin,
          UserRow,
          t
        })}

      {/* Right: chat area */}
      {(!isCoffeePost || showChatInCoffeePost) && (
        <section className={`personal-chat-panel ${userDetails?.id ? '' : 'guest-user'} ${hasHeader ? 'with-header' : ''}`}>
          {selectedUserId || internalSelectedUserId ? (
            <>
              <header className={`personal-chat-header ${isCoffeePost ? 'coffee-post-screen-chat-header' : ''}`}>
                <div className={`personal-chat-header-container ${isCoffeePost ? 'coffee-post-screen-chat-header-container' : ''}`}>
                  {isCoffeePost && showChatInCoffeePost && onBackToPersonalChats && (
                    <button
                      className="back-to-chats-btn"
                      onClick={async () => {
                        // Update unread count before clearing state to ensure it's reset
                        if (currentChatId) {
                          await updatePersonalChatUnreadCount(currentChatId)
                        }
                        // Clear internal state
                        setInternalSelectedUserId(null)
                        setSelectedChatUser(null)
                        setCurrentChatId(null)
                        setChats([])
                        setValue('')
                        setRepliedMessage(null)
                        setIsUserBlocked(false)
                        setAttachments([])
                        setAttachments([])
                        // Reset refs to allow fresh fetch on next selection
                        processingUserIdRef.current = null
                        fetchingChatIdRef.current = null
                        // Call parent callback
                        if (onBackToPersonalChats) {
                          onBackToPersonalChats()
                        }
                      }}
                      aria-label="Back to chats"
                    >
                      <i className="pi pi-arrow-left" />
                    </button>
                  )}
                  <div className={`active-chat-details ${isCoffeePost ? 'coffee-post-active-chat-details' : ''}`}>
                    <div className="personal-chat-avatar">{selectedChatUser?.other_user_avatar_url || selectedChatUser?.users_flag_url ? <img src={selectedChatUser.other_user_avatar_url || selectedChatUser.users_flag_url} alt="user-avatar" loading="lazy" decoding="async" draggable={false} /> : <div className="avatar-text">{(selectedChatUser?.other_user_name || selectedChatUser?.first_name || 'U')[0]}</div>}</div>
                    <div className={`chat-text-content ${isCoffeePost ? 'coffee-post-chat-text' : ''}`}>
                      <div className={`chat-heading ${isCoffeePost ? 'coffee-post-chat-heading' : ''}`}>{selectedChatUser ? selectedChatUser.other_user_name || `${selectedChatUser.first_name || ''} ${selectedChatUser.last_name || ''}`.trim() || 'User' : 'User Name'}</div>
                    </div>
                  </div>
                </div>

                <div className="menu-icon">{<ChatControls isUserBlocked={isUserBlocked} onBlockUser={() => openConfirmationModal('blockUser', selectedChatUser)} onUnblockUser={() => openConfirmationModal('unblockUser', selectedChatUser)} onClearChat={() => openConfirmationModal('clearChat', selectedChatUser)} />}</div>
              </header>

              <div className="personal-chat-messages" ref={containerRef}>
                {isFetchingOlder && (
                  <div className="top-loader show" aria-live="polite" aria-busy="true">
                    <i className="pi pi-spinner pi-spin" />
                    <span>Loading messages…</span>
                  </div>
                )}

                {chats.length > 0 ? (
                  groupedMessages.map(([date, msgs]) => (
                    <div key={date} className="date-group">
                      <div className="date-separator">{!isFetchingOlder && <span className="date-label">{date}</span>}</div>
                      {msgs.map((msg) => {
                        const attachments = Array.isArray(msg.attachments) ? msg.attachments : []

                        // Split attachments so that each image/video with caption becomes its own "virtual" message bubble
                        // Use helper function to determine media attachments
                        const mediaAttachments = attachments.filter((att) => {
                          const type = getAttachmentType(att)

                          return type === 'image' || type === 'video'
                        })
                        const mediaWithCaptions = mediaAttachments.filter((att) => att.caption && att.caption.trim())
                        const mediaWithoutCaptions = mediaAttachments.filter((att) => !att.caption || !att.caption.trim())
                        const docAttachments = attachments.filter((att) => {
                          const type = getAttachmentType(att)

                          return type !== 'image' && type !== 'video'
                        })

                        const virtualMessages = []
                        const hasTextContent = Boolean(msg?.content?.trim())
                        const hasUrlPreview = Boolean(msg?.url_preview)
                        const hasMedia = mediaAttachments.length > 0
                        const hasDocuments = docAttachments.length > 0

                        // If ANY media has a caption, render ALL media individually (not in grid)
                        const hasAnyCaption = mediaWithCaptions.length > 0

                        // Documents with/without captions
                        const docsWithCaptions = docAttachments.filter((att) => att.caption && att.caption.trim())
                        // const docsWithoutCaptions = docAttachments.filter((att) => !att.caption || !att.caption.trim())
                        const hasAnyDocCaption = docsWithCaptions.length > 0

                        // Separate media and documents into different bubbles
                        // Priority: If documents exist, text/URL preview go with documents
                        //          If only media exists, text/URL preview go with media

                        // 1. Documents bubble: includes text, URL preview, and documents
                        if (hasDocuments) {
                          if (hasAnyDocCaption) {
                            // If ANY document has a caption, render ALL documents individually (similar to media)
                            docAttachments.forEach((att, index) => {
                              virtualMessages.push({
                                msg: {
                                  ...msg,
                                  attachments: [att],
                                  // Text and URL preview go with the first document only
                                  content: index === 0 ? msg.content || '' : '',
                                  url_preview: index === 0 ? msg.url_preview || null : null
                                },
                                keySuffix: `document-${index}`,
                                // First document bubble is primary when documents exist
                                isPrimaryForScroll: index === 0
                              })
                            })
                          } else {
                            // No captions on documents: group them into a single bubble (existing behaviour)
                            virtualMessages.push({
                              msg: {
                                ...msg,
                                attachments: docAttachments,
                                // Text and URL preview go with documents
                                content: msg.content || '',
                                url_preview: msg.url_preview || null
                              },
                              keySuffix: 'documents',
                              isPrimaryForScroll: true // Documents bubble is primary when it exists
                            })
                          }
                        } else if ((hasTextContent || hasUrlPreview) && !hasMedia) {
                          // Only text/URL preview, no attachments
                          virtualMessages.push({
                            msg: {
                              ...msg,
                              attachments: [],
                              content: msg.content || '',
                              url_preview: msg.url_preview || null
                            },
                            keySuffix: 'text-only',
                            isPrimaryForScroll: true
                          })
                        }

                        // 2. Media bubbles: separate from documents
                        if (hasMedia) {
                          if (hasAnyCaption) {
                            // If ANY media has a caption, render ALL media individually
                            mediaAttachments.forEach((att, index) => {
                              const originalIdx = attachments.indexOf(att)

                              virtualMessages.push({
                                msg: {
                                  ...msg,
                                  attachments: [att],
                                  // Include text/URL preview with first media only if no documents
                                  content: !hasDocuments && index === 0 ? msg.content || '' : '',
                                  url_preview: !hasDocuments && index === 0 ? msg.url_preview || null : null,
                                  _attachmentIndex: originalIdx
                                },
                                keySuffix: `media-${index}`,
                                // First media bubble becomes primary scroll target if no documents
                                isPrimaryForScroll: !hasDocuments && index === 0
                              })
                            })
                          } else {
                            // No captions: render media in grid (all media together)
                            virtualMessages.push({
                              msg: {
                                ...msg,
                                attachments: mediaWithoutCaptions,
                                // Include text/URL preview with media grid if no documents
                                content: !hasDocuments ? msg.content || '' : '',
                                url_preview: !hasDocuments ? msg.url_preview || null : null
                              },
                              keySuffix: 'media-grid',
                              // Media grid becomes primary scroll target if no documents
                              isPrimaryForScroll: !hasDocuments
                            })
                          }
                        }

                        // If for some reason we didn't create any virtual messages, fall back to the original
                        const messagesToRender = virtualMessages.length > 0 ? virtualMessages : [{ msg, keySuffix: 'single', isPrimaryForScroll: true }]

                        return messagesToRender.map(({ msg: renderMsg, keySuffix, isPrimaryForScroll }) => {
                          const isOutgoing = (renderMsg?.userid || renderMsg?.sender_id) === userDetails?.id

                          const incomingMenuNode = !isOutgoing ? <MessageMenu placement="bottom-start" isActionAllowed={false} isAdminUser={false} reportAbuseOption={true} isOwnMessage={false} hasAttachments={false} isReportedByMe={false} isPendingApproval={false} allowPersonalChat={false} onReply={() => handleReply(renderMsg)} onForward={() => openForwardModal(renderMsg)} onEdit={() => openEditModal(renderMsg)} onDeleteForMe={() => openConfirmationModal('deleteForMe', renderMsg)} onDeleteForEveryone={() => openConfirmationModal('deleteForEveryone', renderMsg)} hasUrl={false} /> : null

                          const ALLOWED_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds
                          const messageTimestamp = renderMsg?.timestamp ? new Date(renderMsg.timestamp).getTime() : null
                          const isActionAllowed = isOutgoing

                          const domId = isPrimaryForScroll ? `msg-${msg.id}` : `msg-${msg.id}-${keySuffix}`

                          return (
                            <div key={`${msg.id}-${keySuffix}`} className={`msg ${isOutgoing ? 'out' : 'in'}`} id={domId}>
                              <PersonalChatMessage msg={renderMsg} isOutgoing={isOutgoing} formatTime={formatTime} onAttachmentClick={openViewer} onReplyClick={scrollToMessage} menuNode={incomingMenuNode} />
                              {isOutgoing && (
                                <div className={`message-options ${renderMsg.pending ? 'invisible' : 'visible'}`}>
                                  <MessageMenu placement="bottom-end" isActionAllowed={isActionAllowed} messageTime={messageTimestamp} allowedDuration={ALLOWED_DURATION} isAdminUser={false} reportAbuseOption={true} isOwnMessage={true} hasAttachments={(renderMsg?.attachments?.length > 0 && renderMsg?.attachments[0]?.caption) || renderMsg?.content} isReportedByMe={false} isPendingApproval={false} allowPersonalChat={false} onReply={() => handleReply(renderMsg)} onForward={() => openForwardModal(renderMsg)} onEdit={() => openEditModal(renderMsg)} onDeleteForMe={() => openConfirmationModal('deleteForMe', renderMsg)} onDeleteForEveryone={() => openConfirmationModal('deleteForEveryone', renderMsg)} hasUrl={false} />
                                </div>
                              )}
                            </div>
                          )
                        })
                      })}
                    </div>
                  ))
                ) : (
                  <div className="no-messages-message">
                    <p>{t('NO_MESSAGE_TITLE')}</p>
                  </div>
                )}
              </div>

              {attachments.length > 0 && !isUploadingImage ? (
                <AttachmentPreview
                  attachments={attachments}
                  scrollToBottom={() => {
                    if (containerRef.current) {
                      containerRef.current.scrollTop = containerRef.current.scrollHeight
                    }
                  }}
                  removeAttachment={removeAttachment}
                  setAttachments={setAttachments}
                  addFiles={addFiles}
                  handleMediaSelected={handleMediaSelected}
                  handleDocumentSelected={handleDocumentSelected}
                  onSendAttachments={sendMessage}
                  onUpdateAttachment={onUpdateCaption}
                  isUploadingImage={isUploadingImage}
                />
              ) : (
                <div className={`personal-chat-input ${isCoffeePost ? 'coffee-post-screen-chat-input' : ''}`}>
                  {(() => {
                    if (!userDetails?.id || showRedirectText) {
                      return (
                        <div className="redirect-login-text">
                          Please <span>{t('LOGIN_AND_CREATE_ACCOUNT')}</span> to Join the Conversation
                        </div>
                      )
                    }

                    const currentChat = currentChatId ? personalChats.find((chat) => String(chat.chat_id || chat.id) === String(currentChatId)) : null
                    const isBlockedByOtherUser = selectedChatUser?.is_blocked_by_other === true || currentChat?.is_blocked_by_other === true

                    if (isBlockedByOtherUser) {
                      return (
                        <div className="blocked-message-content">
                          <div className="blocked-message-text">
                            <i className="pi pi-ban"></i>
                            <span>{t('BLOCKED_BY_USER_MESSAGE')}</span>
                          </div>
                        </div>
                      )
                    }

                    if (isUserBlocked) {
                      return (
                        <div className="blocked-message-content">
                          <Button label="Clear chat" icon="pi pi-minus-circle" className="delete-btn" onClick={() => openConfirmationModal('clearChat', selectedChatUser)} />
                          <Button label="Unblock" icon="pi pi-ban" className="unblock-btn" onClick={() => openConfirmationModal('unblockUser', selectedChatUser)} />
                        </div>
                      )
                    }

                    return (
                      <div className={`input-wrapper ${isCoffeePost ? 'coffee-post-input-wrapper' : ''}`}>
                        {/* URL Preview in Input Area */}
                        {inputPreviewUrl && (
                          <URLPreview
                            metadata={inputPreviewMetadata}
                            isLoading={isInputMetadataLoading && !inputPreviewMetadata}
                            onClose={() => {
                              setInputPreviewUrl(null)
                              setInputPreviewMetadata(null)
                            }}
                            showCloseButton={true}
                            className="input-url-preview"
                            isInInputArea={true}
                          />
                        )}
                        {repliedMessage && (
                          <div className="reply-preview">
                            <div className="reply-header-container">
                              <div className="reply-header">
                                <p className="replying-to">{repliedMessage?.sender_id === userDetails?.id ? 'Replying to you' : `Replying to ${repliedMessage?.user_info?.first_name || 'User'}`}</p>
                              </div>

                              <div className="reply-attachment-container">
                                {repliedMessage?.attachments?.length > 0 &&
                                  (() => {
                                    const firstAttachment = repliedMessage.attachments[0]
                                    const attachmentType = getAttachmentType(firstAttachment)

                                    return (
                                      <div className="reply-body with-attachment">
                                        <div className="attachment-info">
                                          <div className="photo-info">
                                            {attachmentType === 'image' && <i className="pi pi-camera"></i>}
                                            {attachmentType === 'video' && <i className="pi pi-video"></i>}
                                            {attachmentType === 'document' && <i className="pi pi-file-pdf"></i>}

                                            <span>
                                              {attachmentType === 'image' && 'Photo'}
                                              {attachmentType === 'video' && 'Video'}
                                              {attachmentType === 'document' && 'Document'}
                                              {repliedMessage.attachments.length > 1 ? ` (+${repliedMessage.attachments.length - 1})` : ''}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  })()}
                              </div>

                              {/* <p className="reply-caption">{repliedMessage?.content || ''}</p> */}
                              <p className="reply-caption">{repliedMessage?.content || repliedMessage?.attachments?.[0]?.caption || ''}</p>
                            </div>
                            {/* <Button className="cancel-reply-btn" icon="pi pi-times" onClick={() => setRepliedMessage(null)}></Button> */}

                            <div className="reply-attachment-image-container">
                              <div>{repliedMessage?.attachments?.[0] && getAttachmentType(repliedMessage.attachments[0]) === 'image' && <img src={repliedMessage.attachments[0]?.attachment_url || repliedMessage.attachments[0]?.media_url || repliedMessage.attachments[0]?.url} className="reply-attachment-thumbnail" alt="attachment thumbnail" />}</div>
                              <button className="cancel-reply-btn" onClick={() => setRepliedMessage(null)}>
                                ✕
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="input-area">
                          <AttachmentMenu setShowRedirectText={!userDetails?.id ? setShowRedirectText : false} ref={uploaderRef} onMediaSelected={handleMediaSelected} onDocumentSelected={handleDocumentSelected} isCoffeePost={isCoffeePost} />
                          <div className="input-area-container">
                            <div>
                              <img src="https://coffeeweb.s3.ap-south-1.amazonaws.com/Logo+Mark.png" alt="coffee-web-logo" className="input-logo" />
                            </div>
                            <InputTextarea ref={inputRef} rows={isWebView ? 1 : 2} cols={1} value={value} onKeyDown={(e) => handleKeyDown(e)} onPaste={(e) => handlePaste(e)} onChange={(e) => handleChange(e.target.value)} placeholder="Type a message..." className={`input-field ${isCoffeePost ? 'coffee-post-input' : ''}`} />
                          </div>
                          <Button rounded icon="pi pi-send send-icon" className={`send-button ${isCoffeePost ? 'coffee-post-screen-send-button' : ''} ${value.length > 0 ? 'has-input-value' : ''}`} onClick={sendMessage} disabled={!value.trim() && attachments.length === 0} />
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
            </>
          ) : (
            <div className="no-chat-selected">
              <div className="no-chat-selected-content">
                <i className="pi pi-comments message-icon" />
                <p>{t('START_MESSAGING')}</p>
              </div>
            </div>
          )}
        </section>
      )}

      {viewer.open && (
        <div className="attachment-viewer-overlay">
          <div className={`attachment-viewer-container ${isFullscreen ? 'fullscreen-mode' : ''}`}>
            {(() => {
              // Get current attachment to check attachment_type as fallback
              const currentAtt = viewer.attachments?.[viewer.currentIndex]
              const attachmentType = currentAtt?.attachment_type || ''
              // Check if image: mime_type starts with 'image' OR attachment_type is 'image'
              const isImage = viewer.type?.startsWith('image') || attachmentType === 'image'

              return <AttachmentViewerControls isImage={isImage} canZoomIn={zoomLevel < 5} canZoomOut={zoomLevel > 1} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onClose={closeViewer} />
            })()}
            <div className="attachment-viewer-content">{viewerModalContent()}</div>
          </div>
        </div>
      )}

      {(() => {
        let modalTitle = 'Confirm'

        if (modalAction === 'deleteForMe') {
          modalTitle = 'Delete Message For Me'
        } else if (modalAction === 'deleteForEveryone') {
          modalTitle = 'Delete Message For Everyone'
        } else if (modalAction === 'clearChat') {
          modalTitle = 'Clear Chat'
        } else if (modalAction === 'blockUser') {
          modalTitle = `Block ${selectedChatUser ? selectedChatUser.other_user_name || `${selectedChatUser.first_name || ''} ${selectedChatUser.last_name || ''}`.trim() || 'User' : 'User Name'}`
        } else if (modalAction === 'unblockUser') {
          modalTitle = `Unblock ${selectedChatUser ? selectedChatUser.other_user_name || `${selectedChatUser.first_name || ''} ${selectedChatUser.last_name || ''}`.trim() || 'User' : 'User Name'}`
        }

        return <DialogUIModal customClassName="confirmation-modal-personal-chats" title={modalTitle} showModal={confirmationModal} bodyContent={confirmationModalContent} footerContent={footerContent} contentWidth="450px" handleClose={() => setConfirmationModal(false)} />
      })()}

      <ForwardMessageModal forwardModalVisible={forwardModalVisible} setForwardModalVisible={setForwardModalVisible} messageToForward={messageToForward} setMessageToForward={setMessageToForward} personalChats={personalChats} selectedUserId={selectedUserId} internalSelectedUserId={internalSelectedUserId} onForward={handleForwardToUsers} onForwardToCommunities={handleForwardToCommunities} communities={communities} isAdminUser={isAdminUser} isForwarding={isForwarding} />

      <EditMessageModal editModalVisible={editModalVisible} setEditModalVisible={setEditModalVisible} messageToEdit={messageToEdit} onSave={handleEditSave} formatTime={formatTime} />
    </div>
  )
}

export default PersonalChat
